import { CommonModule, CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { environment } from '../../../environments/environment';
import { PrimaryButtonComponent } from '../../basic/buttons/primary-button/primary-button.component';
import { TextImageModalComponent } from '../../basic/modal/text-image-modal/text-image-modal.component';
import { ToLocaleFormat } from '../../basic/pipes/to-locale-format.pipe';
import { TruncatePipe } from '../../basic/pipes/truncate.pipe';
import { BoardService } from '../../core/board/board.service';
import { SessionService } from '../../core/session/session.service';
import { InvoiceStripe } from '../../core/stripe/invoice.stripe';
import { StripeService } from '../../core/stripe/stripe.service';
import { UserClerkService } from '../../core/user/user-clerk.service';
import { WorkspaceHolderService } from '../../core/workspace/workspace.holder.service';
import { BoardIconComponent } from '../../domain/board/board-icon/board-icon.component';
import { UpdateSubscriptionButtonComponent } from '../../domain/membership/update-subscription-button/update-subscription-button.component';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';

@Component({
  selector: 'app-usage-billing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzUploadModule,
    NzModalModule,
    NzPopconfirmModule,
    NzAlertModule,
    NzSkeletonModule,

    CurrencyPipe,

    TruncatePipe,
    ToLocaleFormat,
    MatTooltip,
    BoardIconComponent,
    TextImageModalComponent,
    BasicPageComponent,
    PrimaryButtonComponent,
    UpdateSubscriptionButtonComponent,
  ],
  templateUrl: './usage-billing.component.html',
  styleUrl: './usage-billing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageBillingComponent {
  constructor(
    private router: Router,
    private msg: NzMessageService,
    private userClerkService: UserClerkService,
    private sessionService: SessionService,
    private boardService: BoardService,
    private stripeService: StripeService,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  isLoading = false;

  sessionList = toSignal(
    this.sessionService.getSessionList$(DateTime.local().startOf('month'), DateTime.local().endOf('month')),
    { initialValue: [] }
  );

  invoiceList = toSignal(this.stripeService.getInvoiceList$(), { initialValue: [] });

  ngOnInit() {}

  getProduct(productId?: string) {
    return environment.stripe.productList.find((product) => product.id === productId);
  }

  getTime(timeSeconds?: number) {
    if (!timeSeconds) {
      return '';
    }
    const date = DateTime.fromSeconds(timeSeconds);
    return date.toFormat('ff');
  }

  get product() {
    return this.getProduct(this.stripeService.getSubscription()?.plan.product);
  }

  get activeInvoiceList() {
    return this.stripeService.getActiveInvoiceList();
  }

  get availableCallCount() {
    return this.stripeService.availableCallCount();
  }

  get availableMemberCount() {
    return this.stripeService.availableMemberCount();
  }

  userList = toSignal(this.boardService.getAllUserList$(), { initialValue: [] });

  onSubscribeClick() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/pricing']));
    window.open(url, '_blank');
  }

  onDownloadInvoiceClick(data: InvoiceStripe) {
    window.open(data.invoice_pdf, '_blank');
  }
}
